import { useProctoredAttempt } from '../../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import useContentProtection from '../../hooks/useContentProtection';
import { 
  ArrowLeft,
  Award, 
  ChevronRight, 
  CheckCircle2, 
  Play,
  BookOpen,
  Clock,
  Trophy,
  FileText,
  Sparkles,
  Target,
  AlertCircle,
  Loader2,
  Volume2
} from 'lucide-react';

const RPATGlobalModuleDetail = () => {
  const { region } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [module, setModule] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('topics');
  
  // Assessment state
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);
  useProctoredAttempt({ active: Boolean(currentAssessment) && !assessmentResult, label: 'assessment' });
  
  // Lesson state
  const [lessonOpen, setLessonOpen] = useState(false);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const fetchModule = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/rpat/global/modules/${region}`);
      setModule(res.data);
    } catch (err) {
      console.error('Failed to fetch module:', err);
      setError('Failed to load module');
    } finally {
      setLoading(false);
    }
  }, [api, region]);

  useEffect(() => {
    if (region) {
      fetchModule();
    }
  }, [fetchModule, region]);

  const handleStartTopic = async (topic) => {
    try {
      await api.post(`/rpat/global/topics/${topic.id}/start`);
      // Fetch full topic content
      const res = await api.get(`/rpat/global/topics/${topic.id}`);
      setCurrentTopic(res.data);
      setCurrentSectionIndex(0);
      setLessonOpen(true);
    } catch (err) {
      toast.error('Failed to start topic');
    }
  };

  const handleCompleteTopic = async () => {
    if (!currentTopic) return;
    
    try {
      await api.post(`/rpat/global/topics/${currentTopic.id}/complete`);
      toast.success('Topic completed!');
      setLessonOpen(false);
      setCurrentTopic(null);
      fetchModule();
    } catch (err) {
      toast.error('Failed to complete topic');
    }
  };

  const handleStartAssessment = async (assessmentId) => {
    try {
      const res = await api.get(`/rpat/global/assessments/${region}/${assessmentId}`);
      setCurrentAssessment(res.data);
      setAssessmentAnswers({});
      setAssessmentResult(null);
      setAssessmentOpen(true);
    } catch (err) {
      toast.error('Failed to load assessment');
    }
  };

  const handleSubmitAssessment = async () => {
    if (!currentAssessment) return;
    
    setSubmitting(true);
    try {
      const res = await api.post(`/rpat/global/assessments/${currentAssessment.id}/submit`, {
        answers: assessmentAnswers
      });
      setAssessmentResult(res.data);
      
      if (res.data.passed) {
        toast.success(`Assessment passed with ${res.data.score_percentage}%!`);
      } else {
        toast.error(`Score: ${res.data.score_percentage}%. ${res.data.score_percentage < 80 ? 'Need 80% to pass.' : ''}`);
      }
      
      fetchModule();
    } catch (err) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClaimBadge = async () => {
    try {
      const res = await api.post(`/rpat/global/badges/${region}/claim`);
      toast.success('Badge earned! Congratulations!');
      fetchModule();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim badge');
    }
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !module) {
    return (
      <MainLayout>
        <div className="p-6">
          <Button variant="ghost" onClick={() => navigate('/rpat/global')} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Global Modules
          </Button>
          <Card className="max-w-2xl mx-auto">
            <CardContent className="pt-12 pb-8 text-center">
              <AlertCircle className="h-16 w-16 mx-auto text-red-400 mb-4" />
              <h2 className="text-2xl font-bold mb-2">Module Not Found</h2>
              <p className="text-muted-foreground">{error || 'The requested module does not exist.'}</p>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const { topics, user_progress, badge_eligibility } = module;
  const completedCount = topics?.filter(t => t.status === 'completed').length || 0;
  const totalTopics = topics?.length || 0;
  const progressPct = user_progress?.overall_progress_percentage || 0;

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/rpat/global')} className="mb-2">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Global Modules
        </Button>

        {/* Header Card */}
        <Card className="overflow-hidden">
          <div className={`h-3 bg-gradient-to-r ${module.color}`} />
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <span className="text-5xl">{module.flag_emoji}</span>
                <div>
                  <CardTitle className="text-2xl">{module.name}</CardTitle>
                  <CardDescription className="mt-1">{module.description}</CardDescription>
                </div>
              </div>
              {user_progress?.badge_earned ? (
                <Badge className="bg-emerald-500 text-white">
                  <Trophy className="h-4 w-4 mr-1" />
                  Badge Earned
                </Badge>
              ) : badge_eligibility?.eligible ? (
                <Button onClick={handleClaimBadge} className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600">
                  <Award className="mr-2 h-4 w-4" />
                  Claim Badge
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <BookOpen className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                <p className="text-2xl font-bold">{totalTopics}</p>
                <p className="text-xs text-muted-foreground">Topics</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 mx-auto mb-1 text-emerald-600" />
                <p className="text-2xl font-bold">{completedCount}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <Clock className="h-5 w-5 mx-auto mb-1 text-amber-600" />
                <p className="text-2xl font-bold">{module.total_hours}h</p>
                <p className="text-xs text-muted-foreground">Duration</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <Target className="h-5 w-5 mx-auto mb-1 text-purple-600" />
                <p className="text-2xl font-bold">{Math.round(progressPct)}%</p>
                <p className="text-xs text-muted-foreground">Progress</p>
              </div>
            </div>
            <Progress value={progressPct} className="h-2" />
          </CardContent>
        </Card>

        {/* Badge Eligibility Card */}
        {!user_progress?.badge_earned && (
          <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-amber-100">
                  <Award className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{module.badge_name}</h3>
                  {badge_eligibility?.eligible ? (
                    <p className="text-sm text-emerald-600">
                      <CheckCircle2 className="inline h-4 w-4 mr-1" />
                      You&apos;re eligible! Claim your badge now.
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {badge_eligibility?.reason || `Complete all topics and pass the final assessment to earn this badge.`}
                    </p>
                  )}
                </div>
                {badge_eligibility && !badge_eligibility.eligible && (
                  <div className="text-sm text-right">
                    <span className="text-muted-foreground">
                      {badge_eligibility.completed || 0}/{badge_eligibility.total || totalTopics} topics
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Topics List */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Course Topics</h2>
          <div className="space-y-3">
            {topics?.map((topic, index) => {
              const isCompleted = topic.status === 'completed';
              const isAssessmentTopic = topic.is_assessment_topic;
              
              return (
                <Card 
                  key={topic.id}
                  className={`transition-all ${isCompleted ? 'bg-emerald-50/50 border-emerald-200' : 'hover:shadow-md'}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isCompleted 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <span className="font-semibold">{index + 1}</span>
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-semibold flex items-center gap-2">
                          {topic.code} - {topic.title}
                          {isAssessmentTopic && (
                            <Badge variant="secondary" className="text-xs">
                              <FileText className="h-3 w-3 mr-1" />
                              Final Exam
                            </Badge>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">{topic.description}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {topic.duration_minutes} min
                          </span>
                          {topic.learning_objectives?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Target className="h-3 w-3" />
                              {topic.learning_objectives.length} objectives
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        {isAssessmentTopic ? (
                          <Button 
                            variant={isCompleted ? "outline" : "default"}
                            onClick={() => handleStartAssessment(`${region}-assess-final`)}
                            data-testid={`start-assessment-${topic.id}`}
                          >
                            {isCompleted ? 'Retake' : 'Start'} Exam
                          </Button>
                        ) : (
                          <Button 
                            variant={isCompleted ? "outline" : "default"}
                            onClick={() => handleStartTopic(topic)}
                            data-testid={`start-topic-${topic.id}`}
                          >
                            <Play className="mr-2 h-4 w-4" />
                            {isCompleted ? 'Review' : 'Start'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Lesson Dialog */}
        <Dialog open={lessonOpen} onOpenChange={(open) => {
          if (!open) stopSpeaking();
          setLessonOpen(open);
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                {currentTopic?.code} - {currentTopic?.title}
              </DialogTitle>
              <DialogDescription>
                Section {currentSectionIndex + 1} of {currentTopic?.content_sections?.length || 0}
              </DialogDescription>
            </DialogHeader>
            
            {currentTopic?.content_sections?.[currentSectionIndex] && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    {currentTopic.content_sections[currentSectionIndex].title}
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      if (isSpeaking) {
                        stopSpeaking();
                      } else {
                        speakText(currentTopic.content_sections[currentSectionIndex].ai_script);
                      }
                    }}
                  >
                    <Volume2 className={`h-4 w-4 mr-2 ${isSpeaking ? 'text-blue-500' : ''}`} />
                    {isSpeaking ? 'Stop' : 'Listen'}
                  </Button>
                </div>
                
                {/* Key Points */}
                {currentTopic.content_sections[currentSectionIndex].key_points?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {currentTopic.content_sections[currentSectionIndex].key_points.map((point, i) => (
                      <Badge key={i} variant="secondary">{point}</Badge>
                    ))}
                  </div>
                )}
                
                {/* Content */}
                <div className="prose prose-sm max-w-none bg-muted/50 p-4 rounded-lg whitespace-pre-wrap">
                  {currentTopic.content_sections[currentSectionIndex].ai_script}
                </div>
                
                {/* Progress */}
                <Progress 
                  value={(currentSectionIndex + 1) / currentTopic.content_sections.length * 100} 
                  className="h-2"
                />
              </div>
            )}
            
            <DialogFooter className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => setCurrentSectionIndex(Math.max(0, currentSectionIndex - 1))}
                disabled={currentSectionIndex === 0}
              >
                Previous
              </Button>
              
              {currentSectionIndex < (currentTopic?.content_sections?.length || 0) - 1 ? (
                <Button onClick={() => setCurrentSectionIndex(currentSectionIndex + 1)}>
                  Next Section
                </Button>
              ) : (
                <Button onClick={handleCompleteTopic} className="bg-emerald-500 hover:bg-emerald-600">
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Complete Topic
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assessment Dialog */}
        <Dialog open={assessmentOpen} onOpenChange={setAssessmentOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {currentAssessment?.title}
              </DialogTitle>
              <DialogDescription>
                {assessmentResult 
                  ? `Score: ${assessmentResult.score_percentage}% (${assessmentResult.correct_count}/${assessmentResult.total_questions})`
                  : `Answer all questions. ${currentAssessment?.pass_percentage}% required to pass.`
                }
              </DialogDescription>
            </DialogHeader>
            
            {assessmentResult ? (
              // Show Results
              <div className="space-y-4">
                <div className={`text-center p-6 rounded-lg ${
                  assessmentResult.passed 
                    ? 'bg-emerald-50 border border-emerald-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  {assessmentResult.passed ? (
                    <>
                      <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-2" />
                      <h3 className="text-xl font-bold text-emerald-700">Assessment Passed!</h3>
                      <p className="text-emerald-600">Score: {assessmentResult.score_percentage}%</p>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-2" />
                      <h3 className="text-xl font-bold text-red-700">Not Passed</h3>
                      <p className="text-red-600">Score: {assessmentResult.score_percentage}% (Need 80%)</p>
                    </>
                  )}
                </div>
                
                {/* Question Review */}
                <div className="space-y-3">
                  {assessmentResult.results?.map((result, index) => (
                    <div 
                      key={`question-result-${result.question_id || index}`}
                      className={`p-3 rounded-lg border ${
                        result.is_correct 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {result.is_correct ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                        )}
                        <div>
                          <p className="font-medium">Q{index + 1}: {currentAssessment?.questions?.[index]?.question}</p>
                          {!result.is_correct && (
                            <p className="text-sm text-red-600 mt-1">
                              Your answer: {result.user_answer || 'No answer'} | Correct: {result.correct_answer}
                            </p>
                          )}
                          <p className="text-sm text-muted-foreground mt-1">{result.explanation}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // Show Questions
              <div className="space-y-6">
                {currentAssessment?.questions?.map((question, index) => (
                  <div key={question.id} className="space-y-3">
                    <h4 className="font-medium">
                      {index + 1}. {question.question}
                    </h4>
                    
                    <RadioGroup
                      value={assessmentAnswers[question.id] || ''}
                      onValueChange={(value) => setAssessmentAnswers({
                        ...assessmentAnswers,
                        [question.id]: value
                      })}
                    >
                      {question.type === 'true_false' ? (
                        <>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="True" id={`${question.id}-true`} />
                            <Label htmlFor={`${question.id}-true`}>True</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="False" id={`${question.id}-false`} />
                            <Label htmlFor={`${question.id}-false`}>False</Label>
                          </div>
                        </>
                      ) : (
                        question.options?.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center space-x-2">
                            <RadioGroupItem value={option} id={`${question.id}-${optIndex}`} />
                            <Label htmlFor={`${question.id}-${optIndex}`}>{option}</Label>
                          </div>
                        ))
                      )}
                    </RadioGroup>
                  </div>
                ))}
              </div>
            )}
            
            <DialogFooter>
              {assessmentResult ? (
                <Button onClick={() => setAssessmentOpen(false)}>
                  Close
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmitAssessment}
                  disabled={submitting || Object.keys(assessmentAnswers).length < (currentAssessment?.questions?.length || 0)}
                >
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Submit Assessment
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default RPATGlobalModuleDetail;
